<template>
  <div class="cg-gift-card">
    <cg-checkbox v-if="hasBalance" v-model="useBalance" :disabled="disabled">
      Use your {{ balance | priceFilter }} gift-card balance
    </cg-checkbox>

    <button
      type="button"
      class="cg-gift-card__toggle"
      :disabled="disabled"
      @click="showCodeInput = !showCodeInput"
    >
      {{ showCodeInput ? '- Enter a gift-card code' : '+ Enter a gift-card code' }}
    </button>

    <form
      v-if="showCodeInput && !disabled"
      class="cg-gift-card__apply"
      @submit.prevent="applyCode"
    >
      <cg-input
        v-model="code"
        placeholder="Enter code"
        :error="Boolean(errorMessage)"
        :maxlength="64"
        class="cg-gift-card__code-input"
      >
        <template v-if="errorMessage" #error-message>
          {{ errorMessage }}
        </template>
      </cg-input>

      <cg-button :disabled="!canApply" :loading="applying" @click="applyCode">
        Apply
      </cg-button>
    </form>
  </div>
</template>

<script>
import Api from '@/axios/api'

import { CgButton, CgCheckbox, CgInput } from '@corporategiftcom/design-system'

const round = (value) => Math.round(value * 100) / 100

export default {
  name: 'GiftCardBlock',
  components: {
    CgInput,
    CgButton,
    CgCheckbox,
  },
  props: {
    amount: {
      type: [Number, String],
      required: false,
      default: 0,
    },
    useGiftCard: {
      type: Boolean,
      required: false,
      default: false,
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data: () => ({
    balance: 0,
    code: '',
    applying: false,
    errorMessage: null,
    showCodeInput: false,
  }),
  computed: {
    hasBalance () {
      return this.balance > 0
    },
    canApply () {
      return this.code.trim().length >= 6 && !this.applying
    },
    useBalance: {
      get () { return this.useGiftCard && this.hasBalance && !this.disabled },
      set (val) { this.$emit('update:useGiftCard', val && this.hasBalance && !this.disabled) },
    },
    // The share of the order the balance pays, the API prices each leg the same way
    applicableAmount () {
      return round(Math.max(0, Math.min(this.balance, Number(this.amount || 0))))
    },
    coversOrder () {
      return this.applicableAmount > 0 && this.applicableAmount >= round(Number(this.amount || 0))
    },
  },
  watch: {
    applicableAmount: {
      immediate: true,
      handler: function (val) {
        this.$emit('update:applicableAmount', val)
      },
    },
    coversOrder: {
      immediate: true,
      handler: function (val) {
        this.$emit('update:coversOrder', val)
      },
    },
    hasBalance: function (val) {
      if (!val && this.useGiftCard) { this.$emit('update:useGiftCard', false) }
    },
    disabled: {
      immediate: true,
      handler: function (val) {
        if (!val) { return }

        this.showCodeInput = false
        if (this.useGiftCard) { this.$emit('update:useGiftCard', false) }
      },
    },
  },
  created () {
    this.loadBalance()
  },
  methods: {
    loadBalance () {
      return Api.get('/gift-cards/dashboard')
        .then(({ data }) => {
          this.balance = Number(data?.balance ?? 0)
        })
        .catch(() => {
          this.balance = 0
        })
    },
    applyCode () {
      const code = this.code.trim()
      if (this.disabled || !this.canApply) { return }

      this.applying = true
      this.errorMessage = null

      Api.post('/gift-cards/redeem', { code })
        .then(({ data }) => {
          this.balance = Number(data?.balance ?? 0)
          this.code = ''
          this.showCodeInput = false

          if (this.hasBalance) { this.$emit('update:useGiftCard', true) }
        })
        .catch(({ response }) => {
          this.errorMessage = response?.data?.message
            || response?.data?.errors?.code?.at(0)
            || 'Gift Card code is not valid, please try again'
        })
        .finally(() => {
          this.applying = false
        })
    },
  },
}
</script>

<style lang="scss" scoped>
.cg-gift-card {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  width: 100%;

  &__toggle {
    padding: 0;
    border: 0;
    background: transparent;
    font-family: 'Lato-Regular', sans-serif;
    font-weight: 400;
    font-size: 15px;
    line-height: 18px;
    color: #42B77A;
    cursor: pointer;
    width: fit-content;

    &:disabled {
      color: #95979D;
      cursor: not-allowed;
    }
  }

  &__apply {
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    gap: 12px;
    width: 100%;
  }

  &__code-input {
    flex: 1 1 auto;
    min-width: 0;
  }
}
</style>
